import { useLazyQuery, useMutation } from '@apollo/client'
import moment from 'moment'
import React from 'react'
import { Badge, Modal, Spinner, Button, Form, Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { toast } from 'react-toastify'
import {
  GET_CHANNEL_COMMUNICATION_LOGS,
  SYNC_TWILIO_MESSAGES_NOW,
} from '../../../gql/queries/channelConfigAdminQuery'
import { DateInput } from '../../../components/DateInput'

interface IChannelMessagesModalProps {
  isOpen: boolean
  onClose: () => void
  phoneNumber?: string
  channelKind?: 'SMS' | 'WHATSAPP' | 'EMAIL'
  ownerLabel?: string
}

const STATUS_OPTIONS = ['SENT', 'DELIVERED', 'READ', 'FAILED', 'RECEIVED']
const DIRECTION_OPTIONS = [
  { value: 'INBOUND', label: 'Inbound' },
  { value: 'OUTBOUND', label: 'Outbound' },
]

const getStatusBadge = (status: string) => {
  const s = (status || '').toUpperCase()
  if (s === 'SENT') return 'primary'
  if (s === 'DELIVERED') return 'info'
  if (s === 'READ') return 'success'
  if (s === 'FAILED') return 'danger'
  if (s === 'RECEIVED') return 'warning'
  return 'secondary'
}

const isTwilioSyncable = (channelKind?: string) => channelKind === 'SMS' || channelKind === 'WHATSAPP'

export const ChannelMessagesModal: React.FC<IChannelMessagesModalProps> = ({
  isOpen,
  onClose,
  phoneNumber,
  channelKind,
  ownerLabel,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('')
  const [directionFilter, setDirectionFilter] = React.useState('')
  const [createdAfter, setCreatedAfter] = React.useState<string | null>(null)
  const [createdBefore, setCreatedBefore] = React.useState<string | null>(null)
  const [lastSync, setLastSync] = React.useState<{ created?: number; updated?: number } | null>(null)
  const [allEdges, setAllEdges] = React.useState<any[]>([])

  const [fetchLogs, { data, loading, error, fetchMore }] = useLazyQuery(GET_CHANNEL_COMMUNICATION_LOGS, {
    fetchPolicy: 'network-only',
  })
  const [syncTwilioNow, { loading: syncing }] = useMutation(SYNC_TWILIO_MESSAGES_NOW)

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const baseVariables = React.useMemo(
    () => ({
      channelPhoneNumber: phoneNumber,
      channel: channelKind,
      content: debouncedSearch || undefined,
      status: statusFilter || undefined,
      direction: directionFilter || undefined,
      createdAfter: createdAfter || undefined,
      createdBefore: createdBefore || undefined,
    }),
    [phoneNumber, channelKind, debouncedSearch, statusFilter, directionFilter, createdAfter, createdBefore]
  )

  const runSearch = React.useCallback(async () => {
    if (!phoneNumber) return
    if (isTwilioSyncable(channelKind)) {
      try {
        const result = await syncTwilioNow({ variables: { channelPhoneNumber: phoneNumber, channelKind } })
        const payload = result.data?.syncTwilioMessagesNow
        if (payload?.error) {
          toast.warning(`Could not sync with Twilio: ${payload.error}`)
        } else if (payload?.success) {
          setLastSync({ created: payload.created, updated: payload.updated })
        }
      } catch (e: any) {
        toast.warning('Could not sync with Twilio, showing local history.')
      }
    }
    const res = await fetchLogs({ variables: baseVariables })
    setAllEdges(res.data?.allCommunications?.edges || [])
  }, [phoneNumber, channelKind, baseVariables, fetchLogs, syncTwilioNow])

  React.useEffect(() => {
    if (isOpen && phoneNumber) {
      runSearch()
    }
    if (!isOpen) {
      setAllEdges([])
      setLastSync(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, phoneNumber])

  const handleLoadMore = async () => {
    const endCursor = data?.allCommunications?.pageInfo?.endCursor
    if (!endCursor) return
    const res = await fetchMore({ variables: { ...baseVariables, after: endCursor } })
    setAllEdges((prev) => [...prev, ...(res.data?.allCommunications?.edges || [])])
  }

  const hasNextPage = data?.allCommunications?.pageInfo?.hasNextPage

  return (
    <Modal className='modal-dialog-communication-logs' show={isOpen} onHide={onClose} size='xl' scrollable centered>
      <Modal.Header closeButton>
        <Modal.Title className='m-0'>
          {channelKind} Messages
          {phoneNumber && <span className='text-muted fs-7 ms-2'>{phoneNumber}</span>}
          {ownerLabel && <div className='text-muted fs-8'>{ownerLabel}</div>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: '400px' }}>
        <div className='d-flex flex-wrap align-items-end gap-2 mb-3'>
          <Form.Group style={{ minWidth: '220px', flex: '1 1 220px' }}>
            <Form.Label className='fs-8 fw-semibold text-gray-700 mb-1'>Search text</Form.Label>
            <Form.Control
              type='text'
              size='sm'
              placeholder='Search in message content...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>
          <Form.Group style={{ minWidth: '140px' }}>
            <Form.Label className='fs-8 fw-semibold text-gray-700 mb-1'>Status</Form.Label>
            <Form.Select size='sm' value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value=''>All</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group style={{ minWidth: '140px' }}>
            <Form.Label className='fs-8 fw-semibold text-gray-700 mb-1'>Direction</Form.Label>
            <Form.Select size='sm' value={directionFilter} onChange={(e) => setDirectionFilter(e.target.value)}>
              <option value=''>All</option>
              {DIRECTION_OPTIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <div style={{ minWidth: '140px' }}>
            <DateInput
              id='channel-messages-created-after'
              name='createdAfter'
              label={<span className='fs-8 fw-semibold text-gray-700'>From</span>}
              value={createdAfter}
              onChange={(_field, value) => setCreatedAfter(value || null)}
            />
          </div>
          <div style={{ minWidth: '140px' }}>
            <DateInput
              id='channel-messages-created-before'
              name='createdBefore'
              label={<span className='fs-8 fw-semibold text-gray-700'>To</span>}
              value={createdBefore}
              onChange={(_field, value) => setCreatedBefore(value || null)}
            />
          </div>
          <Button variant='primary' size='sm' onClick={runSearch} disabled={syncing}>
            {syncing ? <Spinner animation='border' size='sm' className='me-1' /> : <i className='bi bi-search me-1' />}
            Search
          </Button>
        </div>

        {isTwilioSyncable(channelKind) && (
          <div className='text-muted fs-8 mb-3'>
            <i className='bi bi-arrow-repeat me-1' />
            {syncing
              ? 'Syncing with Twilio...'
              : lastSync
              ? `Synced with Twilio just now (${lastSync.created || 0} new, ${lastSync.updated || 0} updated).`
              : 'Automatically syncs with Twilio when searching.'}
          </div>
        )}

        {loading && allEdges.length === 0 ? (
          <div className='d-flex flex-column align-items-center justify-content-center p-5'>
            <Spinner animation='border' variant='primary' />
            <span className='mt-2 text-muted'>Loading messages...</span>
          </div>
        ) : error ? (
          <div className='alert alert-danger'>
            <i className='bi bi-exclamation-triangle-fill me-2'></i>
            Error loading messages: {error.message}
          </div>
        ) : allEdges.length === 0 ? (
          <div className='text-center p-5'>
            <i className='bi bi-chat-left-text text-muted fs-1 mb-3 d-block'></i>
            <p className='text-muted'>No messages found for this channel with the current filters.</p>
          </div>
        ) : (
          <div className='table-responsive'>
            <Table hover size='sm' className='align-middle'>
              <thead>
                <tr className='text-muted fw-bold fs-8 text-uppercase'>
                  <th>Date</th>
                  <th>Direction</th>
                  <th>Status</th>
                  <th>Contact</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {allEdges.map(({ node }: any) => {
                  const isOutbound = node.direction === 'OUTBOUND'
                  const contact = node.contact
                  return (
                    <tr key={node.id}>
                      <td className='text-nowrap fs-8'>{moment(node.created).format('MMM DD, YYYY hh:mm A')}</td>
                      <td>
                        <Badge bg={isOutbound ? 'light-primary' : 'light-success'} text='dark' className='fs-9'>
                          {isOutbound ? 'Outbound' : 'Inbound'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={getStatusBadge(node.status)} className='fs-9'>
                          {node.status}
                        </Badge>
                      </td>
                      <td className='fs-8'>
                        {contact ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() : '—'}
                      </td>
                      <td className='fs-8 text-break' style={{ maxWidth: '400px' }}>
                        <OverlayTrigger overlay={<Tooltip>{node.content || 'No content'}</Tooltip>}>
                          <span className='text-truncate d-inline-block' style={{ maxWidth: '400px' }}>
                            {node.content || <span className='fst-italic text-muted'>No content</span>}
                          </span>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
            {hasNextPage && (
              <div className='text-center mt-2'>
                <Button variant='light-primary' size='sm' onClick={handleLoadMore} disabled={loading}>
                  {loading ? <Spinner animation='border' size='sm' className='me-1' /> : null}
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} size='sm'>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
